/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';

import { useContext } from '@hooks';
import { mergeClasses } from '@utils';

import { LinksLinkContext } from './Links.Link';

/**
 * Links.Link.Icon component that represents an icon within a `Links.Link` component.
 * Must be used as a child of a `Links.Link` component.
 *
 * @component Links.Link.Icon
 * @param {Object} props - The properties for the Links.Link.Icon component.
 * @param {React.ReactNode} props.children - The icon element to display within the Links.Link.Icon component. Must be a valid React element.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the icon container.
 * @param {Object} [restProps] - Additional properties to be passed to the icon container element.
 * @returns {ReactElement} The rendered Links.Link.Icon component.
 * @example
 * <Links>
 *   <Links.Link href="/example1">
 *     Example 1
 *     <Links.Link.Icon>
 *       <svg>...</svg>
 *     </Links.Link.Icon>
 *   </Links.Link>
 * </Links>
 */
export default function LinksLinkIcon({ children, className, ...restProps }) {
  useContext(
    LinksLinkContext,
    'Links.Link.Icon must be a child of Links.Link.'
  );

  return (
    <span
      className={mergeClasses(
        'box-border flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-green to-[#E0F65A] p-1 text-black transition-opacity md:h-12 md:w-12 md:p-2 md:opacity-0 md:group-hover:opacity-100',
        className
      )}
      {...restProps}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            className: mergeClasses(child.props.className, 'h-full w-full')
          });
        }
        throw new Error(
          'Links.Link.Icon children must be valid React elements'
        );
      })}
    </span>
  );
}

LinksLinkIcon.propTypes = {
  children: PropTypes.element.isRequired,
  className: PropTypes.string
};

LinksLinkIcon.defaultProps = {
  className: ''
};
